/**
 * Everything that may federate into this platform, in one table.
 *
 * Two record types share it, because they answer one question. A
 * `FederationConnection` is the platform's own identity-provider toggle
 * (`finixt-sso` → the Finixt identity provider); a `FederatedPartner` is an outside organization with
 * a master token (`relix-oidc` → Relix Identity). The design draws both in the
 * same list under Connection / Provider / Status, and an operator asking "who
 * can get in" does not care which table the row came from.
 *
 * Neither record links to the other in the API — a connection carries `id`,
 * `label` and `enabled` and nothing else — so the two are merged here for
 * display only, and each keeps its own actions.
 *
 * A partner's lifecycle lives on its sub-page, not on the row: rotate and
 * revoke are consequential, and the token they mint is shown once. Approving a
 * pending partner is not here at all — that is the review queue's job.
 */
import { ApiError } from '@optum/api-client/client';
import { listFederatedPartners } from '@optum/api-client/endpoints/federated-partners';
import {
  disableConnection,
  enableConnection,
  listConnections,
} from '@optum/api-client/endpoints/federation';
import type { FederationConnection } from '@optum/api-client/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useRecordPanel } from '../../components/shell/record.js';
import { useOpenRecordPage } from '../../components/shell/record-page.js';
import { EmptyBlock, ErrorBlock, LoadingBlock } from '../../ui/AsyncState.js';
import { Button } from '../../ui/Button.js';
import { StatusBadge } from '../../ui/StatusBadge.js';
import { Icon } from '../../ui/finixt.js';
import { useToast } from '../../ui/useToast.js';
import { qk } from '../../lib/query-keys.js';
import { DataTable } from '../../ui/DataTable.js';

export function ConnectionsTab() {
  const panel = useRecordPanel();
  const openPage = useOpenRecordPage();
  const connections = useQuery({ queryKey: qk.federation(), queryFn: listConnections });
  const partners = useQuery({
    queryKey: qk.federatedPartners(),
    queryFn: () => listFederatedPartners(),
  });

  if (connections.isPending || partners.isPending) return <LoadingBlock />;
  if (connections.isError) {
    return (
      <ErrorBlock
        message={connections.error instanceof Error ? connections.error.message : 'Failed to load'}
      />
    );
  }

  const connectionRows = connections.data ?? [];
  // Pending partners are the review queue's business until a decision lands.
  const partnerRows = (partners.data ?? []).filter((p) => p.status !== 'pending');
  const total = connectionRows.length + partnerRows.length;

  if (total === 0) {
    return <EmptyBlock>No connections or partners yet.</EmptyBlock>;
  }

  return (
    <>
      {partners.isError && <ErrorBlock message="Could not load federated partners." />}
      <DataTable
        count={`${total} connection${total === 1 ? '' : 's'}`}
        hint="partner approvals live in the review queue"
        rows={[
          ...connectionRows.map((conn) => ({
            key: `c-${conn.id}`,
            kind: 'connection' as const,
            conn,
          })),
          ...partnerRows.map((partner) => ({
            key: `p-${partner.id}`,
            kind: 'partner' as const,
            partner,
          })),
        ]}
        rowKey="key"
        selectedKey={panel.current?.kind === 'partner' ? `p-${panel.current.id}` : undefined}
        onRowClick={(row) => {
          if (row.kind === 'partner') panel.open({ kind: 'partner', id: row.partner.id });
        }}
        onRowOpenPage={(row) => {
          if (row.kind === 'partner') openPage({ kind: 'partner', id: row.partner.id });
        }}
        columns={[
          {
            key: 'id',
            label: 'Connection',
            mono: true,
            strong: true,
            render: (row) => (row.kind === 'connection' ? row.conn.id : row.partner.idp_name),
          },
          {
            key: 'label',
            label: 'Provider',
            render: (row) => (row.kind === 'connection' ? row.conn.label : row.partner.name),
          },
          {
            key: 'status',
            label: 'Status',
            render: (row) =>
              row.kind === 'connection' ? (
                <StatusBadge status={row.conn.enabled ? 'enabled' : 'disabled'} />
              ) : (
                <StatusBadge status={row.partner.status} vocabulary="federatedPartner" />
              ),
          },
          {
            key: 'actions',
            label: 'Actions',
            align: 'right',
            stopRowClick: true,
            render: (row) =>
              row.kind === 'connection' ? (
                <ConnectionToggle conn={row.conn} />
              ) : (
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 5,
                    fontSize: 'var(--text-meta-lg)',
                    color: 'var(--text-muted)',
                  }}
                >
                  Manage
                  <Icon name="chevron_right" size={14} />
                </span>
              ),
          },
        ]}
      />
    </>
  );
}

function ConnectionToggle({ conn }: { conn: FederationConnection }) {
  const qc = useQueryClient();
  const toast = useToast();
  const toggle = useMutation({
    mutationFn: () => (conn.enabled ? disableConnection(conn.id) : enableConnection(conn.id)),
    onSuccess: () => {
      toast.success(`${conn.enabled ? 'Disabled' : 'Enabled'} ${conn.label}`);
      qc.invalidateQueries({ queryKey: qk.federation() });
    },
    onError: (e) => toast.error('Toggle failed', e instanceof ApiError ? e.message : String(e)),
  });
  return (
    <Button
      size="sm"
      variant={conn.enabled ? 'danger' : 'primary'}
      onClick={() => toggle.mutate()}
      disabled={toggle.isPending}
    >
      {toggle.isPending ? '…' : conn.enabled ? 'Disable' : 'Enable'}
    </Button>
  );
}
